import { ReceiptLong } from "@mui/icons-material";
import { Button, SvgIcon } from "@mui/material";
import { PaymentVerificationDialog } from "./payment-verification-dialog";
import { ChangeEvent, FC, useState } from "react";
import { GetFileByIdResults } from "~/queries/useGetFileById";
import { useBoolean } from "usehooks-ts";
import toast from "react-hot-toast";
import { FILE_UPLOAD_TYPE, PAYMENT_VERIFICATION_UPLOAD_STATUS } from "~/types/__generated/gql/graphql";
import { useGetFileUploadUrl } from "~/queries/useGetFileUploadUrl";
import { useCreatePaymentVerification } from "~/queries/useCreatePaymentVerification";
import { GetCurrentCompanyResults } from "~/queries/useGetCurrentCompany";
import axios from "axios";
import { useUpdateFile } from "~/queries/useUpdateFile";
interface FileAttachment {
  file: File;
  preview: string | null;
  type: string;
}
export const PaymentVerificationButton: FC<{
  company: GetCurrentCompanyResults;
  file: GetFileByIdResults;
}> = ({
  company,
  file
}) => {
  const updateFile = useUpdateFile();
  const getFileUploadUrl = useGetFileUploadUrl();
  const createPaymentVerification = useCreatePaymentVerification();
  const [uploads, setUploads] = useState<FileAttachment[] | undefined>();
  const {
    value: isPaymentVerificationOpen,
    setTrue: openPaymentVerificationUpload,
    setFalse: closePaymentVerificationUpload
  } = useBoolean(false);
  const handleUploadPaymentVerification = async (paymentUploadStatus: PAYMENT_VERIFICATION_UPLOAD_STATUS, receivedAmount: number, receivedDate?: Date) => {
    await toast.promise(
    // eslint-disable-next-line no-async-promise-executor
    new Promise(async (resolve, reject) => {
      try {
        if (!file || !uploads) return reject(new Error("Something went wrong"));
        const paymentVerifications: string[] = [];
        for (const upload of uploads) {
          const {
            name,
            size,
            type
          } = upload.file;
          const nameArr = name.split(".");
          const {
            uploadUrl,
            filePath
          } = await getFileUploadUrl.mutateAsync({
            type: FILE_UPLOAD_TYPE.PAYMENT_VERIFICATION,
            id: company._id,
            extension: nameArr[nameArr.length - 1],
            mimeType: type
          });
          await axios.put(uploadUrl, upload.file, {
            headers: {
              "Content-Type": type
            },
            transformRequest: data => data
          });
          const paymentVerification = await createPaymentVerification.mutateAsync({
            input: {
              fileId: file?._id,
              attachment: {
                name: name,
                extension: nameArr[nameArr.length - 1],
                size,
                url: filePath
              },
              receivedAmount: receivedAmount,
              ...(receivedDate ? {
                receivedDate: receivedDate.toISOString()
              } : undefined),
              paymentUploadStatus: paymentUploadStatus
            }
          });
          paymentVerifications.push(paymentVerification._id);
        }
        await updateFile.mutateAsync({
          id: file._id,
          input: {
            paymentVerifications: [...(file.paymentVerifications ?? []), ...paymentVerifications]
          }
        });
        // await updateCurrentCompany.mutateAsync({
        // 	input: {
        // 		logo: filePath,
        // 	},
        // });
        resolve(null);
      } catch (e) {
        reject(e instanceof Error ? e : new Error("Something went wrong"));
      }
    }), {
      loading: "Uploading payment verification...",
      success: "Payment verification uploaded successfully",
      error: "Failed to upload payment verification"
    });
  };
  const handleAttachPaymentVerification = (e: ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = Array.from(e.target.files ?? []);
    const newFiles: FileAttachment[] = selectedFiles.map(file => {
      const type = file.type;
      const preview = type.startsWith("image/") || type.startsWith("video/") ? URL.createObjectURL(file) : null;
      return {
        file,
        preview,
        type
      };
    });
    setUploads(newFiles);
    openPaymentVerificationUpload();
  };
  const handleCloseUploadPaymentVerificationDialog = () => {
    setUploads(undefined);
    closePaymentVerificationUpload();
  };
  return <>
			{isPaymentVerificationOpen && <PaymentVerificationDialog file={file} uploadFiles={uploads} handleConfirm={handleUploadPaymentVerification} handleClose={handleCloseUploadPaymentVerificationDialog} />}
			<Button sx={{
      alignContent: "right",
      alignItems: "right",
      justifyContent: "right",
      justifyItems: "right"
    }} component="label" startIcon={<SvgIcon>
						<ReceiptLong />
					</SvgIcon>} data-sentry-element="Button" data-sentry-source-file="payment-verification-button.tsx">
				Upload Payment
				<input type="file" hidden multiple onChange={handleAttachPaymentVerification} />
			</Button>
		</>;
};